import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import {
  api,
  type EditorialItem,
  type MediaAgent,
  type MediaAsset,
  type MediaFormat,
  type MediaJob,
  type MediaSpec,
  type MediaTemplate,
} from '../lib/api';
import { Logo } from './Logo';
import { mediaIdempotencyKey } from '../lib/media-idempotency';

type Area = 'produce' | 'library' | 'templates' | 'renders' | 'agent';
const areas: Array<{ id: Area; name: string }> = [
  { id: 'produce', name: 'Produzir' },
  { id: 'library', name: 'Biblioteca de mídia' },
  { id: 'templates', name: 'Templates' },
  { id: 'renders', name: 'Renderizações' },
  { id: 'agent', name: 'Agente local' },
];
const formatNames: Record<MediaFormat, string> = {
  CARD: 'Card / imagem',
  CAROUSEL: 'Carrossel',
  STORY: 'Story',
  COVER: 'Capa',
  VIDEO: 'Vídeo / Reel',
};
const stateNames: Record<string, string> = {
  PENDING: 'Aguardando agente',
  WAITING: 'Aguardando retry',
  RENDERING: 'Renderizando',
  RUNNING: 'Em execução',
  VALIDATING: 'Validando',
  READY: 'Pronto',
  SUCCEEDED: 'Concluído',
  FAILED: 'Falhou',
  PAUSED: 'Pausado',
};
const dimensions: Record<MediaFormat, [number, number]> = {
  CARD: [1080, 1080],
  CAROUSEL: [1080, 1350],
  STORY: [1080, 1920],
  COVER: [1080, 1920],
  VIDEO: [1080, 1920],
};

export function MediaWorkspace({
  session,
  onBack,
  initialItem,
}: {
  session: Session;
  onBack: () => void;
  initialItem?: Pick<EditorialItem, 'id' | 'title'> | null;
}) {
  const [area, setArea] = useState<Area>('produce');
  const [templates, setTemplates] = useState<MediaTemplate[]>([]);
  const [specs, setSpecs] = useState<MediaSpec[]>([]);
  const [jobs, setJobs] = useState<MediaJob[]>([]);
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [agents, setAgents] = useState<MediaAgent[]>([]);
  const [items, setItems] = useState<EditorialItem[]>([]);
  const [itemId, setItemId] = useState(initialItem?.id ?? '');
  const [sourceAssetId, setSourceAssetId] = useState('');
  const [format, setFormat] = useState<MediaFormat>('CARD');
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState(initialItem?.title ?? '');
  const [caption, setCaption] = useState('');
  const [variation, setVariation] = useState('');
  const [license, setLicense] = useState('');
  const [source, setSource] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateSlug, setNewTemplateSlug] = useState('');
  const [newTemplateFormat, setNewTemplateFormat] =
    useState<MediaFormat>('CARD');
  const [newTemplateVersion, setNewTemplateVersion] = useState(1);
  const [newTemplateBackground, setNewTemplateBackground] = useState('#152d3c');
  const [newTemplateForeground, setNewTemplateForeground] = useState('#fff9ee');
  const [newTemplateAccent, setNewTemplateAccent] = useState('#e8b66a');
  const [newTemplateSafe, setNewTemplateSafe] = useState(110);
  const [newTemplateTitleTop, setNewTemplateTitleTop] = useState(0.32);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [templateData, specData, jobData, assetData, agentData, memory] =
      await Promise.all([
        api.media.templates(session),
        api.media.specs(session),
        api.media.jobs(session),
        api.media.assets(session),
        api.media.agents(session),
        api.editorial.memory(session),
      ]);
    setTemplates(templateData.templates);
    setSpecs(specData.specs);
    setJobs(jobData.jobs);
    setAssets(assetData.assets);
    setAgents(agentData.agents);
    setItems(memory.items);
  }, [session]);
  useEffect(() => {
    void refresh().catch((cause: unknown) =>
      setError(
        cause instanceof Error
          ? cause.message
          : 'Não foi possível carregar a Fábrica.',
      ),
    );
  }, [refresh]);
  useEffect(() => {
    if (!previewId) {
      setPreviewUrl(null);
      return;
    }
    let active = true;
    let url: string | null = null;
    void api.media
      .assetBlob(session, previewId)
      .then((blob) => {
        if (!active) return;
        url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      })
      .catch((cause: unknown) =>
        setError(
          cause instanceof Error ? cause.message : 'Preview indisponível.',
        ),
      );
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [previewId, session]);
  const chosenItem = items.find((item) => item.id === itemId);
  const selectedTemplate = templates.find(
    (template) => template.id === templateId,
  );
  const availableTemplates = useMemo(
    () =>
      templates.filter(
        (template) => template.category === format && template.active,
      ),
    [templates, format],
  );
  useEffect(() => {
    if (!availableTemplates.some((template) => template.id === templateId))
      setTemplateId(availableTemplates[0]?.id ?? '');
  }, [availableTemplates, templateId]);

  async function act(work: () => Promise<unknown>, success: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
      await refresh();
      setNotice(success);
      return true;
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Operação não concluída.',
      );
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function produce() {
    if (!selectedTemplate || (!itemId && !sourceAssetId)) {
      setError('Escolha uma pauta ou um original e um template.');
      return;
    }
    const created = await act(async () => {
      const resolvedTitle = title || chosenItem?.title || 'Mídia original';
      const idempotencyKey = await mediaIdempotencyKey({
        editorialItemId: itemId || null,
        sourceAssetId: sourceAssetId || null,
        format,
        template: selectedTemplate,
        editorialItem: chosenItem ?? null,
        title: resolvedTitle,
        caption,
        variation,
      });
      return api.media.createSpec(session, {
        ...(itemId ? { editorialItemId: itemId } : {}),
        ...(sourceAssetId ? { sourceAssetId } : {}),
        templateId,
        format,
        title: resolvedTitle,
        caption,
        idempotencyKey,
      });
    }, 'Job persistido. O agente local processará quando estiver online.');
    if (created) setArea('renders');
  }
  async function upload() {
    if (!file || !license.trim() || !source.trim()) {
      setError('Selecione o arquivo e informe origem e licença/direitos.');
      return;
    }
    await act(async () => {
      const asset = await api.media.uploadOriginal(
        session,
        file,
        license,
        source,
      );
      setSourceAssetId(asset.id);
      setFile(null);
    }, 'Original importado com checksum e proveniência; ele não será sobrescrito.');
  }
  async function createTemplate() {
    if (!newTemplateName.trim() || !/^[a-z0-9-]{3,60}$/.test(newTemplateSlug)) {
      setError(
        'Informe nome e identificador do template (letras minúsculas, números e hífens).',
      );
      return;
    }
    const [width, height] = dimensions[newTemplateFormat];
    await act(
      () =>
        api.media.createTemplate(session, {
          name: newTemplateName,
          slug: newTemplateSlug,
          version: newTemplateVersion,
          category: newTemplateFormat,
          width,
          height,
          layout: {
            background: newTemplateBackground,
            foreground: newTemplateForeground,
            accent: newTemplateAccent,
            variant: 'editorial',
            safe: newTemplateSafe,
            maxTitle: 100,
            titleTopPct: newTemplateTitleTop,
            labelTopPct: 0.18,
            bodyGap: 55,
            overlayOpacity: 0.13,
          },
        }),
      'Nova versão do template salva e auditada.',
    );
  }
  const previewAsset = assets.find((asset) => asset.id === previewId);
  const originalAssets = assets.filter(
    (asset) => asset.media_role === 'ORIGINAL',
  );
  const jobById = new Map(jobs.map((job) => [job.id, job]));
  const assetsByJob = (jobId: string) =>
    assets.filter((asset) => asset.render_job_id === jobId);

  return (
    <div className="app-shell media-shell">
      <header>
        <Logo />
        <button className="editorial-back" onClick={onBack}>
          ← Voltar
        </button>
      </header>
      <main className="media-main">
        <div className="editorial-intro">
          <div>
            <p className="eyebrow">MISSÃO 3 · FÁBRICA DE CONTEÚDO</p>
            <h1>Do roteiro ao arquivo master.</h1>
            <p>
              Templates, FFmpeg e agente local. Cada peça tem origem, estado e
              histórico.
            </p>
          </div>
          <span className="editorial-badge">Sem publicação externa</span>
        </div>
        <nav className="editorial-tabs" aria-label="Áreas da Fábrica">
          {areas.map((entry) => (
            <button
              key={entry.id}
              className={area === entry.id ? 'selected' : ''}
              onClick={() => setArea(entry.id)}
            >
              {entry.name}
            </button>
          ))}
          <button
            onClick={() =>
              void refresh().catch(() =>
                setError('Não foi possível atualizar.'),
              )
            }
          >
            Atualizar
          </button>
        </nav>
        {error && (
          <div className="error-banner" role="alert">
            {error}
            <button onClick={() => setError(null)}>Fechar</button>
          </div>
        )}
        {notice && (
          <div className="editorial-notice" role="status">
            {notice}
          </div>
        )}

        {area === 'produce' && (
          <section className="media-columns">
            <article className="panel media-form">
              <p className="eyebrow">1 · ORIGEM</p>
              <h2>Pauta editorial e original opcional</h2>
              <p className="media-hint">
                Combine uma pauta aprovada com seu original para publicar em
                modo supervisionado. Também é possível gerar a partir de apenas
                uma das duas origens.
              </p>
              {initialItem && (
                <p className="media-hint">
                  Pauta recebida do Cérebro Editorial: {initialItem.title}
                </p>
              )}
              <label>
                Pauta planejada
                <select
                  value={itemId}
                  onChange={(event) => setItemId(event.target.value)}
                >
                  <option value="">Sem pauta</option>
                  {items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title} · {item.planned_date}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Original importado
                <select
                  value={sourceAssetId}
                  onChange={(event) => setSourceAssetId(event.target.value)}
                >
                  <option value="">Sem original</option>
                  {originalAssets.map((asset) => (
                    <option key={asset.id} value={asset.id}>
                      {typeof asset.metadata.originalFilename === 'string'
                        ? asset.metadata.originalFilename
                        : asset.id}{' '}
                      · {asset.mime_type}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Título da peça
                <input
                  value={title}
                  maxLength={200}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder={chosenItem?.title ?? 'Título'}
                />
              </label>
              <label>
                Legenda / texto derivado
                <textarea
                  value={caption}
                  maxLength={5000}
                  onChange={(event) => setCaption(event.target.value)}
                  placeholder="Texto de acompanhamento, opcional"
                />
              </label>
            </article>
            <article className="panel media-form">
              <p className="eyebrow">2 · ESPECIFICAÇÃO</p>
              <h2>Formato e template</h2>
              <div className="media-format-grid">
                {(Object.keys(formatNames) as MediaFormat[]).map((value) => (
                  <button
                    key={value}
                    className={format === value ? 'selected' : ''}
                    onClick={() => setFormat(value)}
                  >
                    {formatNames[value]}
                  </button>
                ))}
              </div>
              <label>
                Template
                <select
                  value={templateId}
                  onChange={(event) => setTemplateId(event.target.value)}
                >
                  {availableTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} · {template.width}×{template.height}
                    </option>
                  ))}
                </select>
              </label>
              {selectedTemplate && (
                <p className="media-hint">
                  Versão {selectedTemplate.version} · área segura{' '}
                  {selectedTemplate.layout.safe}px ·{' '}
                  {selectedTemplate.layout.variant}
                </p>
              )}
              <label>
                Nova variação (opcional)
                <input
                  value={variation}
                  onChange={(event) =>
                    setVariation(
                      event.target.value
                        .replace(/[^a-zA-Z0-9_-]/g, '')
                        .slice(0, 32),
                    )
                  }
                  placeholder="Deixe vazio para repetir sem duplicar"
                />
              </label>
              <button
                className="primary"
                disabled={busy || !templateId}
                onClick={() => void produce()}
              >
                Criar job de renderização
              </button>
              <p className="media-hint">
                O job não publica. Sem agente online, ele permanece aguardando
                com retry seguro.
              </p>
            </article>
            <article className="panel media-form media-upload">
              <p className="eyebrow">ORIGINAL OPCIONAL</p>
              <h2>Importar sem sobrescrever</h2>
              <input
                type="file"
                accept="image/png,image/jpeg,video/mp4,audio/wav,audio/mpeg"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
              <label>
                Origem / autoria
                <input
                  value={source}
                  onChange={(event) => setSource(event.target.value)}
                  maxLength={160}
                  placeholder="Ex.: gravação própria"
                />
              </label>
              <label>
                Licença / direitos
                <input
                  value={license}
                  onChange={(event) => setLicense(event.target.value)}
                  maxLength={160}
                  placeholder="Ex.: autoria própria"
                />
              </label>
              <button
                className="secondary"
                disabled={busy || !file}
                onClick={() => void upload()}
              >
                Importar original
              </button>
              <p className="media-hint">
                PNG, JPEG, MP4, WAV ou MP3 até 25 MB. MIME é verificado pelos
                bytes; nome do arquivo não vira caminho no storage.
              </p>
            </article>
          </section>
        )}

        {area === 'library' && (
          <section className="editorial-section">
            <div className="editorial-heading">
              <div>
                <p className="eyebrow">ASSETS REAIS</p>
                <h2>Biblioteca de Mídia</h2>
                <p>
                  Original → derivado → final. Pré-visualização sob
                  autenticação.
                </p>
              </div>
              <span>{assets.length} arquivo(s)</span>
            </div>
            <div className="media-library">
              {assets.slice(0, 40).map((asset) => (
                <article className="media-asset-card" key={asset.id}>
                  <div className="media-asset-symbol">
                    {asset.mime_type.startsWith('video/')
                      ? '▶'
                      : asset.mime_type.startsWith('image/')
                        ? '▧'
                        : '≡'}
                  </div>
                  <strong>
                    {asset.media_role} · {asset.asset_type}
                  </strong>
                  <span>
                    {asset.mime_type} ·{' '}
                    {asset.width && asset.height
                      ? asset.width + '×' + asset.height
                      : 'texto/áudio'}
                  </span>
                  <span>
                    {Math.round(asset.size_bytes / 1024)} KB ·{' '}
                    {asset.validation_status}
                  </span>
                  <small>
                    Origem: {asset.source} · licença: {asset.license}
                  </small>
                  {asset.parent_asset_id && (
                    <small>
                      Derivado de {asset.parent_asset_id.slice(0, 8)}…
                    </small>
                  )}
                  <button
                    className="secondary compact"
                    onClick={() => setPreviewId(asset.id)}
                  >
                    Preview / exportar
                  </button>
                </article>
              ))}
            </div>
            {assets.length === 0 && (
              <p className="editorial-empty">
                Nenhum arquivo foi importado ou renderizado ainda.
              </p>
            )}
          </section>
        )}

        {area === 'templates' && (
          <section className="editorial-section">
            <div className="editorial-heading">
              <div>
                <p className="eyebrow">SISTEMA VISUAL EXTENSÍVEL</p>
                <h2>Templates</h2>
                <p>
                  Dimensões, regiões seguras, paleta e versão são persistidas; a
                  identidade definitiva segue aberta.
                </p>
              </div>
            </div>
            <div className="media-library">
              {templates.map((template) => (
                <article
                  className="media-template-card"
                  key={template.id}
                  style={{ borderTopColor: template.layout.accent }}
                >
                  <div
                    className="media-template-swatch"
                    style={{
                      background: template.layout.background,
                      color: template.layout.foreground,
                    }}
                  >
                    <span style={{ color: template.layout.accent }}>●</span>{' '}
                    {template.name}
                  </div>
                  <strong>{formatNames[template.category]}</strong>
                  <span>
                    {template.width} × {template.height} · v{template.version}
                  </span>
                  <small>
                    {template.active ? 'Ativo' : 'Inativo'} · margem{' '}
                    {template.layout.safe}px · {template.layout.variant}
                  </small>
                  {template.user_id && (
                    <button
                      className="secondary compact"
                      disabled={busy}
                      onClick={() =>
                        void act(
                          () =>
                            api.media.setTemplateActive(
                              session,
                              template.id,
                              !template.active,
                            ),
                          template.active
                            ? 'Template desativado.'
                            : 'Template ativado.',
                        )
                      }
                    >
                      {template.active ? 'Desativar' : 'Ativar'}
                    </button>
                  )}
                </article>
              ))}
            </div>
            <div className="panel media-form media-template-editor">
              <p className="eyebrow">SEU TEMPLATE</p>
              <h3>Criar versão própria</h3>
              <p className="media-hint">
                Uma versão existente permanece preservada quando você cria a
                próxima. Os oficiais não são alterados.
              </p>
              <div className="media-template-fields">
                <label>
                  Nome
                  <input
                    value={newTemplateName}
                    maxLength={120}
                    onChange={(event) => setNewTemplateName(event.target.value)}
                    placeholder="Minha identidade"
                  />
                </label>
                <label>
                  Identificador
                  <input
                    value={newTemplateSlug}
                    maxLength={60}
                    onChange={(event) =>
                      setNewTemplateSlug(
                        event.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, ''),
                      )
                    }
                    placeholder="minha-identidade"
                  />
                </label>
                <label>
                  Formato
                  <select
                    value={newTemplateFormat}
                    onChange={(event) =>
                      setNewTemplateFormat(event.target.value as MediaFormat)
                    }
                  >
                    {(Object.keys(formatNames) as MediaFormat[]).map(
                      (entry) => (
                        <option key={entry} value={entry}>
                          {formatNames[entry]}
                        </option>
                      ),
                    )}
                  </select>
                </label>
                <label>
                  Versão
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={newTemplateVersion}
                    onChange={(event) =>
                      setNewTemplateVersion(Number(event.target.value))
                    }
                  />
                </label>
                <label>
                  Fundo
                  <input
                    type="color"
                    value={newTemplateBackground}
                    onChange={(event) =>
                      setNewTemplateBackground(event.target.value)
                    }
                  />
                </label>
                <label>
                  Texto
                  <input
                    type="color"
                    value={newTemplateForeground}
                    onChange={(event) =>
                      setNewTemplateForeground(event.target.value)
                    }
                  />
                </label>
                <label>
                  Destaque
                  <input
                    type="color"
                    value={newTemplateAccent}
                    onChange={(event) =>
                      setNewTemplateAccent(event.target.value)
                    }
                  />
                </label>
                <label>
                  Margem segura (px)
                  <input
                    type="number"
                    min={40}
                    max={220}
                    value={newTemplateSafe}
                    onChange={(event) =>
                      setNewTemplateSafe(Number(event.target.value))
                    }
                  />
                </label>
                <label>
                  Posição do título (20–60%)
                  <input
                    type="number"
                    min={20}
                    max={60}
                    value={Math.round(newTemplateTitleTop * 100)}
                    onChange={(event) =>
                      setNewTemplateTitleTop(Number(event.target.value) / 100)
                    }
                  />
                </label>
              </div>
              <p className="media-hint">
                Saída {dimensions[newTemplateFormat].join('×')} px, sem marca
                d’água. Uma versão nova usa o mesmo identificador e número
                maior.
              </p>
              <button
                className="primary compact"
                disabled={busy}
                onClick={() => void createTemplate()}
              >
                Salvar template
              </button>
            </div>
          </section>
        )}

        {area === 'renders' && (
          <section className="editorial-section">
            <div className="editorial-heading">
              <div>
                <p className="eyebrow">FILA PERSISTENTE</p>
                <h2>Renderizações</h2>
                <p>
                  Correlation ID, tentativas, estado e erros são mantidos no
                  banco.
                </p>
              </div>
            </div>
            <div className="media-renders">
              {specs.map((spec) => {
                const job = jobById.get(spec.job_id);
                const outputs = assetsByJob(spec.job_id);
                return (
                  <article className="panel media-render-row" key={spec.id}>
                    <div>
                      <span className="eyebrow">
                        {formatNames[spec.format]} ·{' '}
                        {stateNames[spec.status] ?? spec.status}
                      </span>
                      <h3>{spec.spec.title}</h3>
                      <p>
                        {new Date(spec.created_at).toLocaleString('pt-BR')} ·
                        job {spec.job_id.slice(0, 8)}… · correlação{' '}
                        {spec.correlation_id.slice(0, 8)}…
                      </p>
                    </div>
                    <div>
                      <strong>
                        {job
                          ? (stateNames[job.status] ?? job.status)
                          : spec.status}
                      </strong>
                      <p>
                        {job?.attempt_count ?? 0}/{job?.max_attempts ?? 3}{' '}
                        tentativas
                      </p>
                      {job?.last_error?.code && (
                        <p className="media-error">{job.last_error.code}</p>
                      )}
                    </div>
                    <div>
                      <p>{outputs.length} asset(s) persistido(s)</p>
                      <div className="media-row-actions">
                        {outputs
                          .filter((asset) => asset.media_role === 'FINAL')
                          .map((asset) => (
                            <button
                              className="secondary compact"
                              key={asset.id}
                              onClick={() => setPreviewId(asset.id)}
                            >
                              Preview{' '}
                              {typeof asset.metadata.page === 'number'
                                ? asset.metadata.page
                                : ''}
                            </button>
                          ))}
                        {(spec.status === 'FAILED' ||
                          job?.status === 'WAITING') && (
                          <button
                            className="secondary compact"
                            disabled={busy}
                            onClick={() =>
                              void act(
                                () => api.media.retry(session, spec.id),
                                'Retry agendado.',
                              )
                            }
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
            {specs.length === 0 && (
              <p className="editorial-empty">
                Nenhuma renderização solicitada.
              </p>
            )}
          </section>
        )}

        {area === 'agent' && (
          <section className="editorial-section">
            <div className="editorial-heading">
              <div>
                <p className="eyebrow">PROCESSAMENTO OUTBOUND</p>
                <h2>Agente local</h2>
                <p>
                  Sem porta pública. Ele inicia heartbeat e busca jobs por
                  HTTPS.
                </p>
              </div>
              <button
                className="secondary"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => api.media.createAgent(session, 'PC principal'),
                    'Agente provisionado. Configure o ID e o segredo somente no ambiente local.',
                  )
                }
              >
                Provisionar agente
              </button>
            </div>
            <div className="media-library">
              {agents.map((agent) => (
                <article className="panel media-agent-card" key={agent.id}>
                  <strong>{agent.name}</strong>
                  <span>
                    {agent.status} ·{' '}
                    {agent.last_seen_at
                      ? new Date(agent.last_seen_at).toLocaleString('pt-BR')
                      : 'Nunca conectado'}
                  </span>
                  <code>{agent.id}</code>
                  <small>
                    Capacidades: {agent.capabilities.join(', ') || 'pendentes'}
                  </small>
                </article>
              ))}
            </div>
            <p className="media-hint">
              No computador do agente, defina CONTROL_PLANE_URL, MEDIA_AGENT_ID
              e LOCAL_AGENT_SHARED_SECRET fora do Git. Execute o build e depois{' '}
              <code>pnpm --filter @gestor/local-agent start</code>. O segredo
              não aparece nesta interface.
            </p>
          </section>
        )}
      </main>
      {previewId && (
        <div
          className="media-preview-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Preview do asset"
        >
          <div className="media-preview-panel">
            <div className="media-preview-head">
              <strong>Preview · {previewAsset?.asset_type}</strong>
              <button onClick={() => setPreviewId(null)}>Fechar ×</button>
            </div>
            {!previewUrl ? (
              <p>Carregando arquivo autenticado…</p>
            ) : previewAsset?.mime_type.startsWith('image/') ? (
              <img src={previewUrl} alt="Preview do asset" />
            ) : previewAsset?.mime_type.startsWith('video/') ? (
              <video src={previewUrl} controls playsInline />
            ) : (
              <a
                href={previewUrl}
                download={
                  previewAsset?.mime_type === 'text/vtt'
                    ? 'legendas.vtt'
                    : 'asset'
                }
              >
                Baixar arquivo textual
              </a>
            )}
            {previewUrl && (
              <a
                className="secondary media-download"
                href={previewUrl}
                download={
                  'gestor-' +
                  previewId +
                  (previewAsset?.mime_type === 'video/mp4'
                    ? '.mp4'
                    : previewAsset?.mime_type === 'text/vtt'
                      ? '.vtt'
                      : previewAsset?.mime_type === 'text/plain'
                        ? '.txt'
                        : '.png')
                }
              >
                Exportar original
              </a>
            )}
            <small>SHA-256: {previewAsset?.checksum_sha256}</small>
          </div>
        </div>
      )}
    </div>
  );
}
